import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Tooltip, useMediaQuery } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DoubleArrowIcon from "@material-ui/icons/DoubleArrow";
import { SunIcon, MoonIcon } from "../DaytimeIcon";
import { cuideoTheme } from "../../../../containers/themes/defaultTheme";

// --- Types ---

interface Day {
  key: string;
  label: string;
  fullLabel: string;
  isWeekday: boolean;
  isWeekend: boolean;
}

export type TimeRange = [string, string];
export type Schedule = Record<string, TimeRange[]>;

export interface WeeklyCalendarProps {
  weekdaysEnabled?: boolean;
  weekendEnabled?: boolean;
  forceMobile?: boolean;
  showSun?: boolean;
  showMoon?: boolean;
  showNightButtons?: boolean;
  value: Schedule;
  onChange: (schedule: Schedule) => void;
}

interface DragState {
  mode: "create" | "move" | "resize-top" | "resize-bottom";
  dayKey: string;
  eventIndex: number;
  initialSlotIndex: number;
  currentSlotIndex: number;
  originalStart: number;
  originalEnd: number;
}

// --- Constants ---

const DAYS: Day[] = [
  { key: "mon", label: "Lun", fullLabel: "Lunes", isWeekday: true, isWeekend: false },
  { key: "tue", label: "Mar", fullLabel: "Martes", isWeekday: true, isWeekend: false },
  { key: "wed", label: "Mié", fullLabel: "Miércoles", isWeekday: true, isWeekend: false },
  { key: "thu", label: "Jue", fullLabel: "Jueves", isWeekday: true, isWeekend: false },
  { key: "fri", label: "Vie", fullLabel: "Viernes", isWeekday: true, isWeekend: false },
  { key: "sat", label: "Sáb", fullLabel: "Sábado", isWeekday: false, isWeekend: true },
  { key: "sun", label: "Dom", fullLabel: "Domingo", isWeekday: false, isWeekend: true },
];

const SLOT_HEIGHT = 20;
const TIME_COLUMN_WIDTH = 52;
const TOTAL_SLOTS = 48;
const DEFAULT_SCROLL_SLOT = 15;

// --- Helpers ---

function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h++) {
    const hh = String(h).padStart(2, "0");
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

function timeToSlotIndex(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 2 + (m >= 30 ? 1 : 0);
}

function slotIndexToTime(index: number): string {
  const h = Math.floor(index / 2);
  const m = index % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
}

function getDayEnabledRange(
  day: Day,
  weekdaysEnabled: boolean,
  weekendEnabled: boolean
): { minSlot: number; maxSlot: number } | null {
  if (!weekdaysEnabled && !weekendEnabled) return null;
  if (weekdaysEnabled && weekendEnabled) return { minSlot: 0, maxSlot: TOTAL_SLOTS };

  let min = TOTAL_SLOTS;
  let max = 0;

  if (weekendEnabled) {
    switch (day.key) {
      case "fri": min = Math.min(min, 30); max = Math.max(max, TOTAL_SLOTS); break;
      case "sat": min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "sun": min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "mon": min = Math.min(min, 0); max = Math.max(max, 16); break;
    }
  }

  if (weekdaysEnabled) {
    switch (day.key) {
      case "sun": min = Math.min(min, 42); max = Math.max(max, TOTAL_SLOTS); break;
      case "mon": case "tue": case "wed": case "thu": case "fri":
        min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "sat": min = Math.min(min, 0); max = Math.max(max, 30); break;
    }
  }

  if (min >= max) return null;
  return { minSlot: min, maxSlot: max };
}

function isDayDisabled(day: Day, weekdaysEnabled: boolean, weekendEnabled: boolean): boolean {
  return getDayEnabledRange(day, weekdaysEnabled, weekendEnabled) === null;
}

function getAvailableCorridor(
  events: TimeRange[],
  excludeIndex: number,
  anchorStart: number,
  anchorEnd: number
): { min: number; max: number } {
  let min = 0;
  let max = TOTAL_SLOTS;
  events.forEach((event, idx) => {
    if (idx === excludeIndex) return;
    const s = timeToSlotIndex(event[0]);
    const e = timeToSlotIndex(event[1]);
    if (e <= anchorStart) min = Math.max(min, e);
    if (s >= anchorEnd) max = Math.min(max, s);
  });
  return { min, max };
}

function computePreviewEvent(
  drag: DragState,
  dayEvents: TimeRange[],
  range?: { minSlot: number; maxSlot: number } | null
): { start: number; end: number } {
  const excludeIndex = drag.mode === "create" ? -1 : drag.eventIndex;
  const anchorStart = drag.mode === "create" ? drag.initialSlotIndex : drag.originalStart;
  const anchorEnd = drag.mode === "create" ? drag.initialSlotIndex + 1 : drag.originalEnd;
  const corridor = getAvailableCorridor(dayEvents, excludeIndex, anchorStart, anchorEnd);
  const cMin = range ? Math.max(corridor.min, range.minSlot) : corridor.min;
  const cMax = range ? Math.min(corridor.max, range.maxSlot) : corridor.max;

  switch (drag.mode) {
    case "create": {
      const rawMin = Math.min(drag.initialSlotIndex, drag.currentSlotIndex);
      const rawMax = Math.max(drag.initialSlotIndex, drag.currentSlotIndex);
      return { start: Math.max(cMin, rawMin), end: Math.min(cMax, rawMax + 1) };
    }
    case "move": {
      const duration = drag.originalEnd - drag.originalStart;
      const offset = drag.initialSlotIndex - drag.originalStart;
      let newStart = drag.currentSlotIndex - offset;
      newStart = Math.max(cMin, Math.min(cMax - duration, newStart));
      return { start: newStart, end: newStart + duration };
    }
    case "resize-top": {
      const newStart = Math.max(cMin, Math.min(drag.originalEnd - 1, drag.currentSlotIndex));
      return { start: newStart, end: drag.originalEnd };
    }
    case "resize-bottom": {
      const newEnd = Math.min(cMax, Math.max(drag.originalStart + 1, drag.currentSlotIndex + 1));
      return { start: drag.originalStart, end: newEnd };
    }
  }
}

function canCopyAllEventsToDay(
  sourceEvents: TimeRange[],
  targetRange: { minSlot: number; maxSlot: number },
  existingEvents: TimeRange[]
): boolean {
  let hasFittingEvent = false;
  for (const event of sourceEvents) {
    const s = Math.max(timeToSlotIndex(event[0]), targetRange.minSlot);
    const e = Math.min(timeToSlotIndex(event[1]), targetRange.maxSlot);
    if (s >= e) continue;
    hasFittingEvent = true;
    const collides = existingEvents.some((r) => {
      const rs = timeToSlotIndex(r[0]);
      const re = timeToSlotIndex(r[1]);
      return s < re && rs < e;
    });
    if (collides) return false;
  }
  return hasFittingEvent;
}

function copyEventsToDay(
  sourceEvents: TimeRange[],
  targetRange: { minSlot: number; maxSlot: number }
): TimeRange[] {
  const result: TimeRange[] = [];
  for (const event of sourceEvents) {
    const s = Math.max(timeToSlotIndex(event[0]), targetRange.minSlot);
    const e = Math.min(timeToSlotIndex(event[1]), targetRange.maxSlot);
    if (s >= e) continue;
    result.push([slotIndexToTime(s), slotIndexToTime(e)]);
  }
  return result;
}

// --- Night helpers ---

const NIGHT_EVENING_START = 42; // 21:00
const NIGHT_MORNING_END = 18; // 09:00
const WEEKDAY_NIGHT_STARTS = ["mon", "tue", "wed", "thu", "fri"];
const WEEKEND_NIGHT_STARTS = ["sat", "sun"];

// Cada noche que arranca en `startDayKeys` ocupa 21:00-24:00 ese día y 00:00-09:00 del día siguiente.
// Los segmentos se recortan al rango habilitado de cada día (los días deshabilitados se omiten).
// Si un segmento colisiona con un evento existente no se crea y no se modifica nada de ese día.
function applyNightSchedule(
  value: Schedule,
  startDayKeys: string[],
  weekdaysEnabled: boolean,
  weekendEnabled: boolean
): Schedule {
  const segments: Record<string, [number, number][]> = {};
  const addSegment = (key: string, start: number, end: number) => {
    (segments[key] ||= []).push([start, end]);
  };
  startDayKeys.forEach((key) => {
    const idx = DAYS.findIndex((d) => d.key === key);
    const nextKey = DAYS[(idx + 1) % DAYS.length].key;
    addSegment(key, NIGHT_EVENING_START, TOTAL_SLOTS);
    addSegment(nextKey, 0, NIGHT_MORNING_END);
  });

  const result: Schedule = { ...value };
  DAYS.forEach((day) => {
    const segs = segments[day.key];
    if (!segs) return;
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const dayEvents = [...(result[day.key] || [])];
    segs.forEach(([s, e]) => {
      const cs = Math.max(s, range.minSlot);
      const ce = Math.min(e, range.maxSlot);
      if (cs >= ce) return;
      const collides = dayEvents.some(([rStart, rEnd]) => {
        const rs = timeToSlotIndex(rStart);
        const re = timeToSlotIndex(rEnd);
        return cs < re && rs < ce;
      });
      if (collides) return;
      dayEvents.push([slotIndexToTime(cs), slotIndexToTime(ce)]);
    });
    if (dayEvents.length) result[day.key] = dayEvents;
  });
  return result;
}

// Elimina los eventos cuya hora de inicio y fin queden dentro de la franja nocturna (21:00-09:00).
function clearNightEvents(value: Schedule): Schedule {
  const result: Schedule = {};
  DAYS.forEach((day) => {
    const events = value[day.key];
    if (!events?.length) return;
    const kept = events.filter(([start, end]) => {
      const s = timeToSlotIndex(start);
      const e = timeToSlotIndex(end);
      const isNight = s >= NIGHT_EVENING_START || e <= NIGHT_MORNING_END;
      return !isNight;
    });
    if (kept.length) result[day.key] = kept;
  });
  return result;
}

// --- Styled components (shared) ---

const MobileList = styled.div`
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  width: 100%;
  overflow: hidden;
`;

const MobileDayBox = styled.div`
  border: 1px solid #c2d1da;
  border-radius: 5px;
  overflow: hidden;
  background: #fff;
  & + & {
    margin-top: 8px;
  }
`;

const MobileDayRow = styled.div<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #e8eff3;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  background: #f5fbff;
`;

const MobileDayLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${cuideoTheme.palette.primary.main};
`;

const MobileEventRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.4rem 1rem 0.4rem 0.5rem;
  border-bottom: 1px solid #e8eff3;
`;

const TimeSelectGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.85rem;
  color: ${cuideoTheme.palette.primary.main};
`;

const TimeSelect = styled.select`
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  font-size: 0.85rem;
  color: ${cuideoTheme.palette.primary.main};
  border: 1px solid #c2d1da;
  border-radius: 4px;
  padding: 4px 2px;
  background: #fff;
  outline: none;
  &:focus { border-color: ${cuideoTheme.palette.secondary.main}; }
`;

const TimeSeparator = styled.span`
  font-size: 0.85rem;
  color: ${cuideoTheme.palette.primary.main};
  padding: 0 4px;
`;

const MobileDeleteButton = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${cuideoTheme.palette.error.main};
  color: #fff;
  font-size: 0.6rem;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: auto;
  &:hover { background: #d43050; }
`;

const AddEventButton = styled.div`
  margin: 0.4rem 1rem;
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  color: ${cuideoTheme.palette.secondary.main};
  border: 1px solid ${cuideoTheme.palette.secondary.main};
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  text-align: center;
  &:hover { background: rgba(91, 192, 190, 0.1); }
`;

const MobileCopyButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid ${({ $disabled }) => ($disabled ? "#c2d1da" : cuideoTheme.palette.primary.main)};
  border-radius: 4px;
  color: ${({ $disabled }) => ($disabled ? "#c2d1da" : cuideoTheme.palette.primary.main)};
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  padding: 2px 8px;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  &:hover {
    background: ${({ $disabled }) => ($disabled ? "none" : "rgba(38, 70, 83, 0.08)")};
  }
`;

const NightButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
`;

const NightButton = styled.button<{ $variant: "filled" | "outlined" }>`
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border: 1px solid ${cuideoTheme.palette.secondary.main};
  ${({ $variant }) =>
    $variant === "filled"
      ? `background: ${cuideoTheme.palette.secondary.main}; color: #fff;`
      : `background: #fff; color: ${cuideoTheme.palette.secondary.main};`}
  &:hover {
    opacity: 0.85;
  }
`;

// --- Styled components (desktop) ---

const Container = styled.div<{ $dragging: boolean }>`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #c2d1da;
  border-radius: 5px;
  margin-top: 0.5rem;
  font-family: "Source Sans Pro", Helvetica, Arial, sans-serif;
  ${({ $dragging }) => $dragging && "user-select: none;"}
`;

const ScrollArea = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const CalendarLayout = styled.div`
  min-width: 500px;
`;

const HeaderBar = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: 3;
`;

const TimeHeader = styled.div`
  width: ${TIME_COLUMN_WIDTH}px;
  flex-shrink: 0;
  background: ${cuideoTheme.palette.primary.main};
  padding: 6px 0;
`;

const DayHeaderCell = styled.div<{ $disabled: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: ${cuideoTheme.palette.primary.main};
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 0;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const CopyDayButton = styled.button<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  color: #fff;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.25 : 0.7)};
  padding: 2px 6px;
  line-height: 1;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 0.25 : 1)};
  }
`;

const BodyArea = styled.div`
  display: flex;
`;

const TimeColumn = styled.div`
  width: ${TIME_COLUMN_WIDTH}px;
  flex-shrink: 0;
`;

const TimeLabel = styled.div<{ $isHour: boolean }>`
  height: ${SLOT_HEIGHT}px;
  box-sizing: border-box;
  font-size: 0.625rem;
  color: ${cuideoTheme.palette.primary.main};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding-right: 4px;
  border-right: 1px solid #c2d1da;
  border-bottom: ${({ $isHour }) => ($isHour ? "1px solid #e8eff3" : "1px solid #c2d1da")};
  background: #f5fbff;
`;

const DayColumn = styled.div<{ $disabled: boolean }>`
  flex: 1;
  position: relative;
  height: ${TOTAL_SLOTS * SLOT_HEIGHT}px;
  border-right: 1px solid #e8eff3;
  background-color: ${({ $disabled }) => ($disabled ? "#eef2f5" : "#fff")};
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0px,
    transparent ${SLOT_HEIGHT - 1}px,
    #e8eff3 ${SLOT_HEIGHT - 1}px,
    #e8eff3 ${SLOT_HEIGHT}px,
    transparent ${SLOT_HEIGHT}px,
    transparent ${2 * SLOT_HEIGHT - 1}px,
    #c2d1da ${2 * SLOT_HEIGHT - 1}px,
    #c2d1da ${2 * SLOT_HEIGHT}px
  );
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  &:last-child { border-right: none; }
`;

const EventBlock = styled.div<{ $preview?: boolean }>`
  position: absolute;
  left: 2px;
  right: 2px;
  box-sizing: border-box;
  background: ${({ $preview }) => ($preview ? "rgba(91, 192, 190, 0.3)" : "rgba(91, 192, 190, 0.6)")};
  border: 1px solid rgba(91, 192, 190, 0.8);
  border-radius: 3px;
  cursor: grab;
  z-index: 1;
  min-height: ${SLOT_HEIGHT}px;
  overflow: hidden;
  &:hover { background: rgba(91, 192, 190, 0.8); }
  &:hover > [data-delete] { opacity: 1; }
`;

const DeleteButton = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${cuideoTheme.palette.error.main};
  color: #fff;
  font-size: 0.55rem;
  line-height: 14px;
  text-align: center;
  cursor: pointer;
  opacity: 0;
  z-index: 3;
  &:hover { background: #d43050; }
`;

const EventLabel = styled.span`
  display: block;
  font-size: 0.85rem;
  line-height: 1.2;
  color: #1a6b6a;
  font-weight: 400;
  padding: 1px 3px;
  pointer-events: none;
  white-space: nowrap;
`;

const ResizeHandle = styled.div<{ $position: "top" | "bottom" }>`
  position: absolute;
  left: 0;
  right: 0;
  height: 5px;
  cursor: ns-resize;
  z-index: 2;
  ${({ $position }) => ($position === "top" ? "top: 0;" : "bottom: 0;")}
`;

const BlockedSlotOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  background-color: #eef2f5;
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0px,
    transparent ${SLOT_HEIGHT - 1}px,
    #e8eff3 ${SLOT_HEIGHT - 1}px,
    #e8eff3 ${SLOT_HEIGHT}px,
    transparent ${SLOT_HEIGHT}px,
    transparent ${2 * SLOT_HEIGHT - 1}px,
    #c2d1da ${2 * SLOT_HEIGHT - 1}px,
    #c2d1da ${2 * SLOT_HEIGHT}px
  );
  pointer-events: none;
  z-index: 0;
`;

// --- Desktop ---

const WeeklyCalendarDesktop: React.FC<WeeklyCalendarProps> = ({
  weekdaysEnabled = false,
  weekendEnabled = false,
  showSun = false,
  showMoon = false,
  value,
  onChange,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dayColumnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const weekdaysEnabledRef = useRef(weekdaysEnabled);
  weekdaysEnabledRef.current = weekdaysEnabled;
  const weekendEnabledRef = useRef(weekendEnabled);
  weekendEnabledRef.current = weekendEnabled;

  const updateDrag = (state: DragState | null) => {
    dragRef.current = state;
    setDragState(state);
  };

  const getSlotFromMouseY = useCallback((e: MouseEvent | React.MouseEvent, dayKey: string): number => {
    const col = dayColumnRefs.current[dayKey];
    if (!col) return 0;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;
    return Math.max(0, Math.min(TOTAL_SLOTS - 1, Math.floor(y / SLOT_HEIGHT)));
  }, []);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const slotIndex = getSlotFromMouseY(e, drag.dayKey);
      if (slotIndex !== drag.currentSlotIndex) {
        updateDrag({ ...drag, currentSlotIndex: slotIndex });
      }
    };

    const handleUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      const day = DAYS.find(d => d.key === drag.dayKey)!;
      const range = getDayEnabledRange(day, weekdaysEnabledRef.current, weekendEnabledRef.current);
      const dayEvents = valueRef.current[drag.dayKey] || [];
      const preview = computePreviewEvent(drag, dayEvents, range);
      const newSchedule = { ...valueRef.current };
      const dayRanges = [...dayEvents];
      const newEvent: TimeRange = [slotIndexToTime(preview.start), slotIndexToTime(preview.end)];

      if (drag.mode === "create") {
        dayRanges.push(newEvent);
      } else {
        dayRanges[drag.eventIndex] = newEvent;
      }

      newSchedule[drag.dayKey] = dayRanges;
      onChangeRef.current(newSchedule);
      updateDrag(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [getSlotFromMouseY]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = DEFAULT_SCROLL_SLOT * SLOT_HEIGHT;
    }
  }, []);

  const handleColumnMouseDown = (e: React.MouseEvent, day: Day) => {
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    if ((e.target as HTMLElement).closest("[data-event]")) return;
    e.preventDefault();
    const slotIndex = getSlotFromMouseY(e, day.key);
    if (slotIndex < range.minSlot || slotIndex >= range.maxSlot) return;
    updateDrag({
      mode: "create",
      dayKey: day.key,
      eventIndex: -1,
      initialSlotIndex: slotIndex,
      currentSlotIndex: slotIndex,
      originalStart: 0,
      originalEnd: 0,
    });
  };

  const handleEventMouseDown = (
    e: React.MouseEvent,
    day: Day,
    idx: number,
    startIdx: number,
    endIdx: number
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (!getDayEnabledRange(day, weekdaysEnabled, weekendEnabled)) return;
    const slotIndex = getSlotFromMouseY(e, day.key);
    updateDrag({
      mode: "move",
      dayKey: day.key,
      eventIndex: idx,
      initialSlotIndex: slotIndex,
      currentSlotIndex: slotIndex,
      originalStart: startIdx,
      originalEnd: endIdx,
    });
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent,
    day: Day,
    idx: number,
    startIdx: number,
    endIdx: number,
    mode: "resize-top" | "resize-bottom"
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (!getDayEnabledRange(day, weekdaysEnabled, weekendEnabled)) return;
    updateDrag({
      mode,
      dayKey: day.key,
      eventIndex: idx,
      initialSlotIndex: mode === "resize-top" ? startIdx : endIdx,
      currentSlotIndex: mode === "resize-top" ? startIdx : endIdx,
      originalStart: startIdx,
      originalEnd: endIdx,
    });
  };

  const handleEventDoubleClick = (day: Day, eventIndex: number) => {
    const newSchedule = { ...value };
    const dayRanges = [...(newSchedule[day.key] || [])];
    dayRanges.splice(eventIndex, 1);
    if (dayRanges.length === 0) {
      delete newSchedule[day.key];
    } else {
      newSchedule[day.key] = dayRanges;
    }
    onChange(newSchedule);
  };

  const handleCopyFromPreviousDay = (dayIndex: number) => {
    if (dayIndex === 0) return;
    const prevDay = DAYS[dayIndex - 1];
    const sourceEvents = value[prevDay.key] || [];
    if (sourceEvents.length === 0) return;
    const day = DAYS[dayIndex];
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const currentEvents = value[day.key] || [];
    const copiedEvents = copyEventsToDay(sourceEvents, range);
    if (copiedEvents.length === 0) return;
    onChange({ ...value, [day.key]: [...currentEvents, ...copiedEvents] });
  };

  return (
    <Container $dragging={!!dragState}>
      <ScrollArea ref={scrollRef}>
        <CalendarLayout>
          <HeaderBar>
            <TimeHeader />
            {DAYS.map((day, dayIndex) => {
              const disabled = isDayDisabled(day, weekdaysEnabled, weekendEnabled);
              const currentEvents = value[day.key] || [];
              const isFirstDay = dayIndex === 0;
              const prevDay = !isFirstDay ? DAYS[dayIndex - 1] : null;
              const prevDayDisabled = prevDay ? isDayDisabled(prevDay, weekdaysEnabled, weekendEnabled) : true;
              const prevDayEvents = prevDay ? (value[prevDay.key] || []) : [];
              const currentRange = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
              const canCopy = !isFirstDay && !disabled && prevDayEvents.length > 0 && !prevDayDisabled && currentRange !== null && canCopyAllEventsToDay(prevDayEvents, currentRange, currentEvents);
              return (
                <DayHeaderCell key={day.key} $disabled={disabled}>
                  {day.fullLabel}
                  <Tooltip title="Copiar del día anterior" arrow placement="top">
                    <CopyDayButton
                      $disabled={!canCopy}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canCopy) handleCopyFromPreviousDay(dayIndex);
                      }}
                    >
                      <DoubleArrowIcon style={{ fontSize: 12 }} />
                      <FileCopyIcon style={{ fontSize: 12 }} />
                      <span style={{ fontSize: "0.65rem", margin: "0 2px" }}>Copiar</span>
                    </CopyDayButton>
                  </Tooltip>
                </DayHeaderCell>
              );
            })}
          </HeaderBar>
          <BodyArea>
            <TimeColumn>
              {TIME_SLOTS.map((slot, slotIndex) => {
                const isDaytime = slotIndex >= 18 && slotIndex < 42;
                return (
                  <TimeLabel key={slot} $isHour={slot.endsWith(":00")}>
                    {isDaytime ? (showSun && <SunIcon />) : (showMoon && <MoonIcon />)}
                    {slot}
                  </TimeLabel>
                );
              })}
            </TimeColumn>
            {DAYS.map((day) => {
              const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
              const disabled = !range;
              const events = value[day.key] || [];
              return (
                <DayColumn
                  key={day.key}
                  ref={(el) => { dayColumnRefs.current[day.key] = el; }}
                  $disabled={disabled}
                  onMouseDown={(e) => handleColumnMouseDown(e, day)}
                >
                  {range && range.minSlot > 0 && (
                    <BlockedSlotOverlay style={{ top: 0, height: range.minSlot * SLOT_HEIGHT }} />
                  )}
                  {range && range.maxSlot < TOTAL_SLOTS && (
                    <BlockedSlotOverlay style={{ top: range.maxSlot * SLOT_HEIGHT, bottom: 0 }} />
                  )}
                  {!disabled &&
                    events.map((event, idx) => {
                      const s = timeToSlotIndex(event[0]);
                      const eIdx = timeToSlotIndex(event[1]);
                      if (dragState?.dayKey === day.key && dragState.eventIndex === idx)
                        return null;
                      return (
                        <EventBlock
                          key={idx}
                          data-event="true"
                          style={{
                            top: s * SLOT_HEIGHT,
                            height: (eIdx - s) * SLOT_HEIGHT,
                          }}
                          onMouseDown={(ev) =>
                            handleEventMouseDown(ev, day, idx, s, eIdx)
                          }
                          onDoubleClick={() =>
                            handleEventDoubleClick(day, idx)
                          }
                        >
                          <EventLabel>{event[0]}-{event[1]}</EventLabel>
                          <DeleteButton
                            data-delete
                            onMouseDown={(ev) => {
                              ev.stopPropagation();
                              ev.preventDefault();
                              handleEventDoubleClick(day, idx);
                            }}
                          >
                            ✕
                          </DeleteButton>
                          <ResizeHandle
                            $position="top"
                            onMouseDown={(ev) =>
                              handleResizeMouseDown(ev, day, idx, s, eIdx, "resize-top")
                            }
                          />
                          <ResizeHandle
                            $position="bottom"
                            onMouseDown={(ev) =>
                              handleResizeMouseDown(ev, day, idx, s, eIdx, "resize-bottom")
                            }
                          />
                        </EventBlock>
                      );
                    })}
                  {dragState?.dayKey === day.key &&
                    (() => {
                      const preview = computePreviewEvent(dragState, events, range);
                      return (
                        <EventBlock
                          $preview
                          data-event="true"
                          style={{
                            top: preview.start * SLOT_HEIGHT,
                            height: (preview.end - preview.start) * SLOT_HEIGHT,
                          }}
                        >
                          <EventLabel>
                            {slotIndexToTime(preview.start)}-{slotIndexToTime(preview.end)}
                          </EventLabel>
                        </EventBlock>
                      );
                    })()}
                </DayColumn>
              );
            })}
          </BodyArea>
        </CalendarLayout>
      </ScrollArea>
    </Container>
  );
};

// --- Mobile ---

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const END_HOUR_OPTIONS = [...HOUR_OPTIONS, "24"];
const MINUTE_OPTIONS = ["00", "30"];

const WeeklyCalendarMobile: React.FC<WeeklyCalendarProps> = ({
  weekdaysEnabled = false,
  weekendEnabled = false,
  showSun = false,
  showMoon = false,
  value,
  onChange,
}) => {
  const handleAddEvent = (day: Day) => {
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const dayRanges = value[day.key] || [];
    for (let s = range.minSlot; s < range.maxSlot - 1; s += 2) {
      const e = Math.min(s + 2, range.maxSlot);
      const overlaps = dayRanges.some((r) => {
        const rs = timeToSlotIndex(r[0]);
        const re = timeToSlotIndex(r[1]);
        return s < re && rs < e;
      });
      if (!overlaps) {
        const newSchedule = { ...value };
        newSchedule[day.key] = [...dayRanges, [slotIndexToTime(s), slotIndexToTime(e)] as TimeRange];
        onChange(newSchedule);
        return;
      }
    }
  };

  const handleDeleteEvent = (day: Day, eventIndex: number) => {
    const newSchedule = { ...value };
    const dayRanges = [...(newSchedule[day.key] || [])];
    dayRanges.splice(eventIndex, 1);
    if (dayRanges.length === 0) {
      delete newSchedule[day.key];
    } else {
      newSchedule[day.key] = dayRanges;
    }
    onChange(newSchedule);
  };

  const handleCopyFromPreviousDay = (dayIndex: number) => {
    if (dayIndex === 0) return;
    const prevDay = DAYS[dayIndex - 1];
    const sourceEvents = value[prevDay.key] || [];
    if (sourceEvents.length === 0) return;
    const day = DAYS[dayIndex];
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const currentEvents = value[day.key] || [];
    const copiedEvents = copyEventsToDay(sourceEvents, range);
    if (copiedEvents.length === 0) return;
    onChange({ ...value, [day.key]: [...currentEvents, ...copiedEvents] });
  };

  const handleTimeChange = (
    day: Day,
    eventIndex: number,
    field: "startH" | "startM" | "endH" | "endM",
    val: string
  ) => {
    const dayRanges = [...(value[day.key] || [])];
    const event = [...dayRanges[eventIndex]] as TimeRange;
    const [startH, startM] = event[0].split(":");
    const [endH, endM] = event[1].split(":");

    let nSH = startH, nSM = startM, nEH = endH, nEM = endM;
    switch (field) {
      case "startH": nSH = val; break;
      case "startM": nSM = val; break;
      case "endH": nEH = val; if (val === "24") nEM = "00"; break;
      case "endM": nEM = val; break;
    }

    const newStartIdx = timeToSlotIndex(`${nSH}:${nSM}`);
    let newEndIdx = timeToSlotIndex(`${nEH}:${nEM}`);

    const isStartChange = field === "startH" || field === "startM";

    // Si al mover la hora de inicio la franja queda invertida (fin <= inicio),
    // desplazamos también la hora fin para mantener la duración original.
    // Al modificar la hora fin dejándola anterior al inicio no se permite el cambio.
    if (newStartIdx >= newEndIdx) {
      if (!isStartChange) return;
      const duration = timeToSlotIndex(event[1]) - timeToSlotIndex(event[0]);
      newEndIdx = newStartIdx + duration;
    }

    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (range && (newStartIdx < range.minSlot || newEndIdx > range.maxSlot)) return;

    const overlaps = dayRanges.some((r, idx) => {
      if (idx === eventIndex) return false;
      const s = timeToSlotIndex(r[0]);
      const e = timeToSlotIndex(r[1]);
      return newStartIdx < e && s < newEndIdx;
    });
    if (overlaps) return;

    dayRanges[eventIndex] = [slotIndexToTime(newStartIdx), slotIndexToTime(newEndIdx)];
    onChange({ ...value, [day.key]: dayRanges });
  };

  return (
    <MobileList>
      {DAYS.map((day, dayIndex) => {
        const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
        const disabled = !range;
        const events = (!disabled && value[day.key]) || [];
        const isFirstDay = dayIndex === 0;
        const prevDay = !isFirstDay ? DAYS[dayIndex - 1] : null;
        const prevDayDisabled = prevDay ? isDayDisabled(prevDay, weekdaysEnabled, weekendEnabled) : true;
        const prevDayEvents = prevDay ? (value[prevDay.key] || []) : [];
        const currentRange = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
        const canCopy = !isFirstDay && !disabled && prevDayEvents.length > 0 && !prevDayDisabled && currentRange !== null && canCopyAllEventsToDay(prevDayEvents, currentRange, events);
        const startHours = range
          ? HOUR_OPTIONS.filter(h => { const n = parseInt(h); return n >= Math.floor(range.minSlot / 2) && n <= Math.floor((range.maxSlot - 1) / 2); })
          : HOUR_OPTIONS;
        const endHours = range
          ? END_HOUR_OPTIONS.filter(h => { const n = parseInt(h); return n >= Math.floor(range.minSlot / 2) && n <= Math.floor(range.maxSlot / 2); })
          : END_HOUR_OPTIONS;
        return (
          <MobileDayBox key={day.key}>
            <MobileDayRow $disabled={disabled}>
              <MobileDayLabel>{day.fullLabel}</MobileDayLabel>
              <MobileCopyButton
                $disabled={!canCopy}
                onClick={() => canCopy && handleCopyFromPreviousDay(dayIndex)}
              >
                <FileCopyIcon style={{ fontSize: 12 }} />
                Copiar del día anterior
              </MobileCopyButton>
            </MobileDayRow>
            {events.map((event, idx) => {
              const [sH, sM] = event[0].split(":");
              const [eH, eM] = event[1].split(":");
              const startSlot = timeToSlotIndex(event[0]);
              const endSlot = timeToSlotIndex(event[1]);
              const startIsDay = startSlot >= 18 && startSlot < 42;
              const endIsDay = endSlot > 18 && endSlot <= 42;
              const hasDaytime = startIsDay || endIsDay;
              const hasNighttime = !startIsDay || !endIsDay;
              return (
                <MobileEventRow key={idx}>
                  {startIsDay ? (
                    <>{hasDaytime && showSun && <SunIcon fontSize={14} />}{hasNighttime && showMoon && <MoonIcon fontSize={14} />}</>
                  ) : (
                    <>{hasNighttime && showMoon && <MoonIcon fontSize={14} />}{hasDaytime && showSun && <SunIcon fontSize={14} />}</>
                  )}
                  <TimeSelectGroup>
                    <TimeSelect value={sH} onChange={(e) => handleTimeChange(day, idx, "startH", e.target.value)}>
                      {startHours.map((h) => <option key={h} value={h}>{h}</option>)}
                    </TimeSelect>
                    :
                    <TimeSelect value={sM} onChange={(e) => handleTimeChange(day, idx, "startM", e.target.value)}>
                      {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </TimeSelect>
                  </TimeSelectGroup>
                  <TimeSeparator>-</TimeSeparator>
                  <TimeSelectGroup>
                    <TimeSelect value={eH} onChange={(e) => handleTimeChange(day, idx, "endH", e.target.value)}>
                      {endHours.map((h) => <option key={h} value={h}>{h}</option>)}
                    </TimeSelect>
                    :
                    <TimeSelect
                      value={eM}
                      disabled={eH === "24"}
                      onChange={(e) => handleTimeChange(day, idx, "endM", e.target.value)}
                    >
                      {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </TimeSelect>
                  </TimeSelectGroup>
                  <MobileDeleteButton onClick={() => handleDeleteEvent(day, idx)}>✕</MobileDeleteButton>
                </MobileEventRow>
              );
            })}
            {!disabled && (
              <AddEventButton onClick={() => handleAddEvent(day)}>+ Añadir franja horaria</AddEventButton>
            )}
          </MobileDayBox>
        );
      })}
    </MobileList>
  );
};

// --- Root ---

const WeeklyCalendar: React.FC<WeeklyCalendarProps> = (props) => {
  const {
    weekdaysEnabled,
    weekendEnabled,
    forceMobile,
    showNightButtons = false,
    value,
    onChange,
  } = props;
  const isDesktop = useMediaQuery(
    `(min-width:${cuideoTheme.breakpoints.values.md}px)`
  );

  useEffect(() => {
    let cleaned = false;
    const newSchedule = { ...value };
    DAYS.forEach((day) => {
      if (!newSchedule[day.key]?.length) return;
      const range = getDayEnabledRange(day, !!weekdaysEnabled, !!weekendEnabled);
      if (!range) {
        delete newSchedule[day.key];
        cleaned = true;
      } else {
        const trimmed = newSchedule[day.key]
          .map(([start, end]: TimeRange): TimeRange | null => {
            const s = Math.max(timeToSlotIndex(start), range.minSlot);
            const e = Math.min(timeToSlotIndex(end), range.maxSlot);
            if (s >= e) return null;
            return [slotIndexToTime(s), slotIndexToTime(e)];
          })
          .filter((r): r is TimeRange => r !== null);
        const original = newSchedule[day.key];
        if (trimmed.length !== original.length || trimmed.some((t, i) => t[0] !== original[i][0] || t[1] !== original[i][1])) {
          if (trimmed.length === 0) {
            delete newSchedule[day.key];
          } else {
            newSchedule[day.key] = trimmed;
          }
          cleaned = true;
        }
      }
    });
    if (cleaned) onChange(newSchedule);
  }, [weekdaysEnabled, weekendEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {showNightButtons && (
        <NightButtonsBar>
          <NightButton
            type="button"
            $variant="filled"
            onClick={() =>
              onChange(
                applyNightSchedule(
                  value,
                  WEEKDAY_NIGHT_STARTS,
                  !!weekdaysEnabled,
                  !!weekendEnabled
                )
              )
            }
          >
            Noches entre semana
          </NightButton>
          <NightButton
            type="button"
            $variant="filled"
            onClick={() =>
              onChange(
                applyNightSchedule(
                  value,
                  WEEKEND_NIGHT_STARTS,
                  !!weekdaysEnabled,
                  !!weekendEnabled
                )
              )
            }
          >
            Noches fin de semana
          </NightButton>
          <NightButton
            type="button"
            $variant="outlined"
            onClick={() => onChange(clearNightEvents(value))}
          >
            Limpiar noches
          </NightButton>
        </NightButtonsBar>
      )}
      {isDesktop && !forceMobile ? (
        <WeeklyCalendarDesktop {...props} />
      ) : (
        <WeeklyCalendarMobile {...props} />
      )}
    </>
  );
};

export default WeeklyCalendar;
